import React, { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import { t } from './i18n';
import { fisierPdf, partajeazaPdf } from './cercetarePdf';

// ============================================================
// Cercetarea sub o tema din „Temele zilei” (18 octombrie 2026, CERCETARE_PLAN.md,
// supabase/cercetare_teme.sql).
//
// Sub tema aleasa: starea cererii pentru agentul de cercetare (la coada, in lucru, asteapta dupa
// tine, gata), intrebarea agentului cu caseta de raspuns, cercetarea pe sectiunile ecranului fix
// din skillul `agent-cercetare` (Pasul 7), nota, linkul ciornei si butoanele.
// Butoanele (Cerceteaza, Scrie postarea, Fa o nota, Lasa deoparte) sunt doar ale agentiei, ca
// „Aleg”; la intrebare raspunde si clientul cu bifa „vede cercetarea” (hotararea Luminitei, 18.10).
// Randurile le scrie laptopul, prin priza; aplicatia doar citeste si cheama doua functii.
// ============================================================

const RETELE = [
  { id: 'facebook', nume: 'Facebook' },
  { id: 'instagram', nume: 'Instagram' },
  { id: 'threads', nume: 'Threads' },
  { id: 'linkedin', nume: 'LinkedIn' },
  { id: 'x', nume: 'X' },
  { id: 'tiktok', nume: 'TikTok' },
];

const STARE_CULORI = {
  la_coada: { background: '#FFF4E5', color: '#7A4508' },
  in_lucru: { background: '#E8EEFB', color: '#1E3A8A' },
  asteapta: { background: '#FDE8E8', color: '#9B1C1C' },
  gata: { background: '#E8F5EE', color: '#14532D' },
};

const sectiune = { display: 'flex', flexDirection: 'column', gap: 6 };
const titluSectiune = { fontSize: 12.5, fontWeight: 700, color: '#1F2937', letterSpacing: '0.4px', textTransform: 'uppercase', marginTop: 4 };
const textCorp = { fontSize: 14.5, color: '#111827', lineHeight: 1.5 };
const textSursa = { fontSize: 13, color: '#374151', lineHeight: 1.45 };

const ora = (iso) => (iso ? new Date(iso).toLocaleString('ro-RO', { timeZone: 'Europe/Bucharest', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }) : '');

function Link({ href, children }) {
  if (!/^https:\/\//.test(href || '')) return <span>{children}</span>;
  return <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#1A6B3C', wordBreak: 'break-word' }}>{children}</a>;
}

function Sursa({ sursa, link, data }) {
  if (!sursa && !link) return null;
  return <div style={textSursa}>{link ? <Link href={link}>{sursa || link}</Link> : sursa}{data ? ` · ${data}` : ''}</div>;
}

function Eticheta({ children, stil }) {
  return <span style={{ fontSize: 12, fontWeight: 700, borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap', ...stil }}>{children}</span>;
}

// Ecranul cercetarii: aceleasi sectiuni, in aceeasi ordine, ca in skill.
export function EcranCercetare({ c, client, nr, titlu }) {
  const k = c.continut || {};
  const lista = (x) => (Array.isArray(x) ? x : []);
  const dosar = lista(k.din_dosar);
  const corecturi = dosar.filter(d => d.stare !== 'se_potriveste').length;
  const vedeClientul = client && client.permisiuni_client ? !!client.permisiuni_client.vede_cercetarea : null;
  const DOSAR = {
    se_potriveste: { text: t('cercetare', 'dosar_se_potriveste'), stil: { background: '#E8F5EE', color: '#14532D' } },
    atentie: { text: t('cercetare', 'dosar_atentie'), stil: { background: '#FFF4E5', color: '#7A4508' } },
    corectat: { text: t('cercetare', 'dosar_corectat'), stil: { background: '#E8EEFB', color: '#1E3A8A' } },
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, borderTop: '1px solid #D1E8DA', paddingTop: 12 }}>
      <div style={sectiune}>
        <div style={{ fontSize: 13, color: '#1F2937', fontWeight: 600 }}>{t('cercetare', 'antet', { client: client.nume, nr })}</div>
        {titlu && <div style={{ fontWeight: 700, fontSize: 16, color: '#111827' }}>{titlu}</div>}
        <div style={textSursa}>
          {t('cercetare', 'cineVede')}{vedeClientul !== null ? ' ' + t('cercetare', vedeClientul ? 'clientulVedeDa' : 'clientulVedeNu') : ''}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {c.gata_la && <Eticheta stil={{ background: '#F3F4F6', color: '#111827' }}>{ora(c.gata_la)}</Eticheta>}
          {Number.isInteger(k.surse_oficiale) && <Eticheta stil={{ background: '#F3F4F6', color: '#111827' }}>{t('cercetare', 'nSurse', { n: k.surse_oficiale })}</Eticheta>}
          <Eticheta stil={{ background: '#F3F4F6', color: '#111827' }}>{t('cercetare', 'nFise', { n: dosar.length })}</Eticheta>
          <Eticheta stil={{ background: '#F3F4F6', color: '#111827' }}>{t('cercetare', 'nFapte', { n: lista(k.fapte).length })}</Eticheta>
          <Eticheta stil={{ background: '#F3F4F6', color: '#111827' }}>{t('cercetare', 'nDeoparte', { n: lista(k.deoparte).length })}</Eticheta>
          {corecturi > 0 && <Eticheta stil={{ background: '#FFF4E5', color: '#7A4508' }}>{t('cercetare', 'nCorecturi', { n: corecturi })}</Eticheta>}
        </div>
      </div>

      {k.pe_scurt && <div style={sectiune}><div style={titluSectiune}>{t('cercetare', 'peScurt')}</div><div style={textCorp}>{k.pe_scurt}</div></div>}

      {lista(k.fapte).length > 0 && (
        <div style={sectiune}>
          <div style={titluSectiune}>{t('cercetare', 'fapte')}</div>
          {k.fapte.map((f, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 2, borderLeft: '3px solid #2E9E5B', paddingLeft: 10 }}>
              <div style={textCorp}><Eticheta stil={{ background: '#E8F5EE', color: '#14532D', marginRight: 6 }}>{t('cercetare', 'oficial')}</Eticheta>{f.text}</div>
              <Sursa sursa={f.sursa} link={f.link} data={f.data} />
            </div>
          ))}
        </div>
      )}

      {lista(k.pe_inteles).length > 0 && (
        <div style={sectiune}>
          <div style={titluSectiune}>{t('cercetare', 'peInteles')}</div>
          {k.pe_inteles.map((p, i) => (
            <div key={i} style={textCorp}>
              <b>{p.termen}</b>: {p.explicatie}
              {typeof p.in_postare === 'boolean' && <div style={textSursa}>{t('cercetare', p.in_postare ? 'inPostareDa' : 'inPostareNu')}{p.de_ce ? ` ${p.de_ce}` : ''}</div>}
            </div>
          ))}
        </div>
      )}

      {dosar.length > 0 && (
        <div style={sectiune}>
          <div style={titluSectiune}>{t('cercetare', 'dinDosar')}</div>
          {dosar.map((d, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={textCorp}>{DOSAR[d.stare] && <Eticheta stil={{ ...DOSAR[d.stare].stil, marginRight: 6 }}>{DOSAR[d.stare].text}</Eticheta>}{d.text}</div>
              {d.corectura && <div style={textSursa}>{t('cercetare', 'corectura')} {d.corectura}</div>}
              {d.fisier && <div style={{ ...textSursa, fontFamily: 'monospace', fontSize: 12.5, wordBreak: 'break-all' }}>{d.fisier}</div>}
            </div>
          ))}
        </div>
      )}

      {lista(k.actori).length > 0 && (
        <div style={sectiune}>
          <div style={titluSectiune}>{t('cercetare', 'actori')}</div>
          {k.actori.map((a, i) => (
            <div key={i}><div style={textCorp}><b>{a.nume}</b>: {a.pozitie}</div><Sursa sursa={a.sursa} link={a.link} /></div>
          ))}
        </div>
      )}

      {lista(k.unghiuri).length > 0 && (
        <div style={sectiune}>
          <div style={titluSectiune}>{t('cercetare', 'unghiuri')}</div>
          <ol style={{ margin: 0, paddingLeft: 20, ...textCorp }}>{k.unghiuri.map((u, i) => <li key={i}>{u}</li>)}</ol>
        </div>
      )}

      {lista(k.riscuri).length > 0 && (
        <div style={{ ...sectiune, background: '#FFF7ED', border: '1px solid #F1D9B5', borderRadius: 10, padding: '10px 12px' }}>
          <div style={{ ...titluSectiune, marginTop: 0, color: '#7A4508' }}>{t('cercetare', 'riscuri')}</div>
          <ul style={{ margin: 0, paddingLeft: 20, ...textCorp }}>{k.riscuri.map((u, i) => <li key={i}>{u}</li>)}</ul>
        </div>
      )}

      {lista(k.poze).length > 0 && (
        <div style={sectiune}>
          <div style={titluSectiune}>{t('cercetare', 'poza')}</div>
          {k.poze.map((p, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={textCorp}>{p.link ? <Link href={p.link}>{p.descriere}</Link> : p.descriere}</div>
              {(p.drept || p.sursa) && <div style={textSursa}>{[p.drept, p.sursa].filter(Boolean).join(' · ')}</div>}
              {p.neconfirmat && <div style={{ ...textSursa, color: '#7A4508' }}>{t('cercetare', 'neconfirmat')} {p.neconfirmat}</div>}
            </div>
          ))}
        </div>
      )}

      {lista(k.deoparte).length > 0 && (
        <div style={{ ...sectiune, background: '#F3F4F6', borderRadius: 10, padding: '10px 12px' }}>
          <div style={{ ...titluSectiune, marginTop: 0 }}>{t('cercetare', 'deoparte')}</div>
          {k.deoparte.map((d, i) => <div key={i} style={textCorp}>{d.text} <span style={{ ...textSursa, fontWeight: 600 }}>({d.motiv})</span></div>)}
        </div>
      )}
    </div>
  );
}

export function CercetareTema({ propunere: x, cercetare: c, client, titlu, rol, onSchimbat, onDeoparte }) {
  const agentia = rol === 'super_admin' || rol === 'manager';
  const nr = x.nr_client ?? x.nr;
  const [deschis, setDeschis] = useState(false);
  const [raspuns, setRaspuns] = useState('');
  const [lucrez, setLucrez] = useState(false);
  const [eroare, setEroare] = useState('');
  const [alegRetele, setAlegRetele] = useState(null);   // null = inchis; [] = deschis
  const [conturi, setConturi] = useState(null);
  const [pdf, setPdf] = useState(null);              // null | 'pregatesc'
  const [pdfFisier, setPdfFisier] = useState(null);  // fisierul facut, pastrat pentru a doua apasare
  const [mesajPdf, setMesajPdf] = useState('');
  // O cercetare refacuta inseamna alt PDF.
  const gataLa = c && c.gata_la;
  useEffect(() => { setPdfFisier(null); setMesajPdf(''); }, [gataLa]);

  const ocupata = c && ['la_coada', 'in_lucru', 'asteapta'].includes(c.stare);
  const areCercetare = !!(c && c.continut);

  async function cere(cerere, retele) {
    setLucrez(true); setEroare('');
    const { error } = await supabase.rpc('cere_lucru_tema', { p_nr: x.nr, p_cerere: cerere, p_retele: retele || null });
    setLucrez(false);
    if (error) { setEroare(t('cercetare', 'nuSaTrimis')); return; }
    setAlegRetele(null);
    onSchimbat();
  }

  async function trimiteRaspuns() {
    if (!raspuns.trim()) return;
    setLucrez(true); setEroare('');
    const { error } = await supabase.rpc('raspunde_cercetare', { p_nr: x.nr, p_text: raspuns });
    setLucrez(false);
    if (error) { setEroare(t('cercetare', 'nuSaTrimis')); return; }
    setRaspuns('');
    onSchimbat();
  }

  // „Trimite PDF”: face fisierul, apoi fereastra de partajare (WhatsApp etc.). Browserul lasa
  // partajarea doar imediat dupa click; daca facerea fisierului a durat prea mult, il tinem gata
  // si cerem inca o apasare, care deschide fereastra pe loc.
  async function trimitePdf() {
    setEroare(''); setMesajPdf('');
    let fisier = pdfFisier;
    if (!fisier) {
      setPdf('pregatesc');
      try {
        fisier = await fisierPdf({ c, client, nr, titlu });
        setPdfFisier(fisier);
      } catch (e) {
        setPdf(null);
        setEroare('PDF-ul nu s-a putut face. Încearcă din nou.');
        return;
      }
    }
    const rezultat = await partajeazaPdf(fisier);
    setPdf(null);
    if (rezultat === 'din_nou') setMesajPdf('PDF-ul e gata. Apasă încă o dată pe „Trimite PDF” ca să alegi WhatsApp.');
    else if (rezultat === 'descarcat') setMesajPdf(`PDF-ul s-a descărcat (${fisier.name}). Îl poți trage în WhatsApp.`);
  }

  async function deschideRetele() {
    setAlegRetele([]);
    if (conturi) return;
    const { data, error } = await supabase.rpc('conturi_client', { p_client_id: Number(client.id) });
    setConturi(error ? [] : [...new Set((data || []).map(r => r.platform))]);
  }

  // Tema aleasa fara cerere (aleasa inainte de 18.10): doar butonul.
  if (!c) {
    if (!agentia || x.stadiu !== 'aleasa') return null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div><button type="button" className="btn-secundar" disabled={lucrez} onClick={() => cere('cercetare')}>{t('cercetare', 'cerceteaza')}</button></div>
        {eroare && <div style={{ fontSize: 13.5, color: '#9B1C1C' }}>{eroare}</div>}
      </div>
    );
  }

  const textStare = c.stare === 'la_coada'
    ? t('cercetare', 'coada_' + c.cerere)
    : c.stare === 'in_lucru' ? t('cercetare', 'inLucru_' + c.cerere)
      : c.stare === 'asteapta' ? t('cercetare', 'asteapta') : t('cercetare', 'gata');
  const faraRetele = conturi && conturi.length === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, background: '#F7FBF8', border: '1px solid #D1E8DA', borderRadius: 12, padding: '10px 12px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{t('cercetare', 'titlu')}</span>
        <Eticheta stil={STARE_CULORI[c.stare]}>{textStare}</Eticheta>
      </div>

      {c.stare === 'la_coada' && (
        <div style={textSursa}>
          {t('cercetare', 'laCoadaExplicatie')}
          {c.raspuns && ' ' + t('cercetare', 'continuaCuRaspuns', { cine: c.raspuns_de_nume || '—' })}
        </div>
      )}
      {c.stare === 'in_lucru' && c.pornita_la && <div style={textSursa}>{t('cercetare', 'pornitaLa', { cand: ora(c.pornita_la) })}</div>}

      {c.stare === 'asteapta' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, background: 'white', border: '1.5px solid #F5B5B5', borderRadius: 10, padding: '10px 12px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#9B1C1C' }}>{t('cercetare', 'intrebareaAgentului', { cand: ora(c.intrebare_la) })}</div>
          <div style={{ ...textCorp, whiteSpace: 'pre-wrap' }}>{c.intrebare}</div>
          <textarea value={raspuns} onChange={e => setRaspuns(e.target.value)} rows={5} placeholder={t('cercetare', 'raspunsPlaceholder')}
            className="cercetare-raspuns"
            style={{ width: '100%', boxSizing: 'border-box', fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: '#111827', border: '1.5px solid #D1E8DA', borderRadius: 10, padding: '9px 11px', resize: 'vertical' }} />
          <div><button type="button" className="btn-principal" disabled={lucrez || !raspuns.trim()} onClick={trimiteRaspuns}>{t('cercetare', 'trimite')}</button></div>
        </div>
      )}

      {c.raspuns && c.stare !== 'asteapta' && c.raspuns_la && (
        <div style={textSursa}>{t('cercetare', 'aRaspuns', { cine: c.raspuns_de_nume || '—', cand: ora(c.raspuns_la) })}</div>
      )}

      {areCercetare && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <button type="button" className={deschis ? 'btn-secundar' : 'btn-principal'} onClick={() => setDeschis(d => !d)} style={{ padding: '8px 16px' }}>
            {t('cercetare', deschis ? 'ascunde' : 'arata')}
          </button>
          <button type="button" className="btn-secundar" style={{ padding: '8px 16px' }} disabled={pdf === 'pregatesc'} onClick={trimitePdf}>
            {pdf === 'pregatesc' ? 'Pregătesc PDF-ul…' : 'Trimite PDF'}
          </button>
        </div>
      )}
      {mesajPdf && <div style={textSursa}>{mesajPdf}</div>}
      {areCercetare && deschis && <EcranCercetare c={c} client={client} nr={nr} titlu={titlu} />}

      {c.nota && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={titluSectiune}>{t('cercetare', 'nota')}</div>
          <div style={{ ...textCorp, whiteSpace: 'pre-wrap', background: 'white', border: '1px solid #D1E8DA', borderRadius: 10, padding: '9px 11px' }}>{c.nota}</div>
        </div>
      )}
      {c.postare_text && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={titluSectiune}>{t('cercetare', 'postareText')}</div>
          <div style={{ ...textCorp, whiteSpace: 'pre-wrap', background: 'white', border: '1px solid #D1E8DA', borderRadius: 10, padding: '9px 11px' }}>{c.postare_text}</div>
        </div>
      )}
      {x.grup_id && <div style={textCorp}><a href={`/?grup=${x.grup_id}`} style={{ color: '#1A6B3C', fontWeight: 600 }}>{t('cercetare', 'deschideCiorna')}</a></div>}

      {agentia && !ocupata && x.stadiu === 'aleasa' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {areCercetare && <button type="button" className="btn-principal" disabled={lucrez} onClick={() => (alegRetele ? setAlegRetele(null) : deschideRetele())}>{t('cercetare', 'scriePostarea')}</button>}
          {areCercetare && <button type="button" className="btn-secundar" disabled={lucrez} onClick={() => cere('nota')}>{t('cercetare', 'faNota')}</button>}
          <button type="button" className="btn-secundar" disabled={lucrez} onClick={onDeoparte}>{t('cercetare', 'lasaDeoparte')}</button>
          <button type="button" className="btn-secundar" disabled={lucrez} onClick={() => cere('cercetare')}>{t('cercetare', areCercetare ? 'cerceteazaDinNou' : 'cerceteaza')}</button>
        </div>
      )}

      {alegRetele && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, background: 'white', border: '1px solid #D1E8DA', borderRadius: 10, padding: '10px 12px' }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: '#111827' }}>{t('cercetare', 'peCeRetele')}</div>
          {conturi === null && <div style={textSursa}>{t('cercetare', 'seIncarca')}</div>}
          {faraRetele && <div style={textSursa}>{t('cercetare', 'faraRetele')}</div>}
          {conturi && conturi.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
              {RETELE.filter(r => conturi.includes(r.id)).map(r => (
                <label key={r.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 15, color: '#111827', cursor: 'pointer' }}>
                  <input type="checkbox" checked={alegRetele.includes(r.id)}
                    onChange={() => setAlegRetele(prev => (prev.includes(r.id) ? prev.filter(z => z !== r.id) : [...prev, r.id]))}
                    style={{ width: 18, height: 18 }} />
                  {r.nume}
                </label>
              ))}
            </div>
          )}
          {conturi !== null && (
            <div><button type="button" className="btn-principal" disabled={lucrez || (!faraRetele && !alegRetele.length)}
              onClick={() => cere('postare', faraRetele ? [] : alegRetele)}>{t('cercetare', 'puneLaCoada')}</button></div>
          )}
        </div>
      )}

      {eroare && <div style={{ fontSize: 13.5, color: '#9B1C1C' }}>{eroare}</div>}
    </div>
  );
}
